import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import { ReactComponent as BackArrow } from '../../../assets/images/backarrow.svg';
import BoonLogo from '../../../assets/images/boon-logo.svg';
import BoonViewIcon from '../../../assets/images/BoonViewIcon.svg';
import { ConnectNetwork } from '../../../components/ConnectNetwork/ConnectNetwork';
import { ContactSelect } from '../../../components/ContactSelect/ContactSelect';
import { FormikTextInput } from '../../../components/FormikTextInput/FormikTextInput';
import NavigationBar from '../../../components/NavigationBar/NavigationBar';
import { ReferralFlowStepper } from '../../../components/ReferralFlowStepper/ReferralFlowStepper';
import { ReferralProcess } from '../../../components/ReferralProcess/ReferralProcess';
import ButtonWithIcon from '../../../components/shared/ButtonWithIcon/ButtonWithIcon';
import { FormikPhoneInput } from '../../../components/shared/FormikPhoneInput/FormikPhoneInput';
import { FormikUrlInput } from '../../../components/shared/FormikUrlInput/FormikUrlInput';
import Header from '../../../components/shared/Header/Header';
import OrSeparator from '../../../components/shared/OrSeparator/OrSeparator';
import { YourInfoSection } from '../../../components/shared/YourInfoSection/YourInfoSection';
import { FileUpload } from '../../../components/FileUpload/FileUpload';
import { useReferralFlowContext } from '../../../contexts/ReferralFlowContext';
import { IsMobile } from '../../../helpers/IsMobile';
import { useCurrentUser } from '../../../hooks/useCurrentUser';

import { CustomQuestions } from './CustomQuestions';

import styles from './CandidateInfo.module.scss';

export const CandidateInfo = () => {
  const { t } = useTranslation('referral');
  const { personalInfo, candidateInfo, jobInfo, setCandidateInfo, setCurrentStep, referralFormPermissions } =
    useReferralFlowContext();
  const { isAuthenticated } = useCurrentUser();
  const [shouldShowConnectNetwork, setShouldShowConnectNetwork] = useState(false);
  const { companyName } = useParams();
  const isMobile = IsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const formikRef = useRef();

  const buildCustomQuestionsValidation = () => {
    const customQuestionsSchema = {};

    if (
      referralFormPermissions.add_referral_form_custom_questions &&
      referralFormPermissions.referral_form_custom_questions
    ) {
      referralFormPermissions.referral_form_custom_questions.forEach((question) => {
        const fieldName = `custom_question_${question.id}`;

        if (question.required) {
          if (question.fieldType === 'single_select_dropdown') {
            customQuestionsSchema[fieldName] = Yup.mixed()
              .required(t('validation.pleaseAnswerQuestion'))
              .test('not-empty', t('validation.pleaseAnswerQuestion'), (value) => {
                const isValid = value !== null && value !== undefined && value !== '';
                return !!isValid;
              });
          } else if (question.fieldType === 'multi_select_dropdown') {
            customQuestionsSchema[fieldName] = Yup.array()
              .required(t('validation.pleaseAnswerQuestion'))
              .test('not-empty', t('validation.pleaseAnswerQuestion'), (value) => {
                const isValid = value && Array.isArray(value) && value.length > 0;
                return Boolean(isValid);
              });
          } else {
            customQuestionsSchema[fieldName] = Yup.string()
              .required(t('validation.pleaseAnswerQuestion'))
              .test('not-empty', t('validation.pleaseAnswerQuestion'), (value) => {
                const isValid = value && value.trim() !== '';
                return Boolean(isValid);
              });
          }
        } else if (question.fieldType === 'single_select_dropdown') {
          customQuestionsSchema[fieldName] = Yup.mixed().optional();
        } else if (question.fieldType === 'multi_select_dropdown') {
          customQuestionsSchema[fieldName] = Yup.array().optional();
        } else {
          customQuestionsSchema[fieldName] = Yup.string().optional();
        }
      });
    }

    return customQuestionsSchema;
  };

  const validationSchema = Yup.object().shape({
    first_name: Yup.string().required(t('validation.pleaseEnterFirstName')),
    last_name: Yup.string().required(t('validation.pleaseEnterLastName')),
    email: Yup.string().email(t('validation.invalidEmailAddress')).required(t('validation.pleaseEnterEmail')),
    phone_number: referralFormPermissions.phone_number_required
      ? Yup.string().required(t('validation.pleaseEnterPhoneNumber'))
      : Yup.string().optional(),
    linkedin_url: referralFormPermissions.linkedin_url_required
      ? Yup.string()
          .test(
            'not-default-url',
            t('validation.pleaseEnterLinkedInUrl'),
            (value) => value && value !== 'www.linkedin.com/in/' && value.trim() !== ''
          )
          .required(t('validation.pleaseEnterLinkedInUrl'))
      : Yup.string().optional(),
    resume_files: referralFormPermissions.resume_required
      ? Yup.array().min(1, t('errors.resumeRequired'))
      : Yup.array().optional(),
    ...buildCustomQuestionsValidation(),
  });

  const buildCustomQuestionsInitialValues = () => {
    const customQuestionsValues = {};

    if (
      referralFormPermissions.add_referral_form_custom_questions &&
      referralFormPermissions.referral_form_custom_questions
    ) {
      referralFormPermissions.referral_form_custom_questions.forEach((question) => {
        const fieldName = `custom_question_${question.id}`;

        if (question.fieldType === 'multi_select_dropdown') {
          customQuestionsValues[fieldName] = candidateInfo?.[fieldName] || [];
        } else {
          customQuestionsValues[fieldName] = candidateInfo?.[fieldName] || '';
        }

        customQuestionsValues[`${fieldName}_fieldType`] = question.fieldType;
      });
    }

    return customQuestionsValues;
  };

  const handleSubmit = (values, { setSubmitting, validateForm }) => {
    validateForm().then((errors) => {
      if (Object.keys(errors).length === 0) {
        const linkedinUrl = values.linkedin_url === 'www.linkedin.com/in/' ? '' : values.linkedin_url;
        setCandidateInfo({ ...values, linkedin_url: linkedinUrl });
        if (jobInfo?.opening && jobInfo?.company) {
          setCurrentStep(4);
        } else {
          setCurrentStep(3);
        }
      }
      setSubmitting(false);
    });
  };

  return (
    <div className="flex w-full">
      {isAuthenticated && !companyName && <NavigationBar />}
      <div className={styles.outerContainer}>
        <Header
          isPublic={!isAuthenticated || companyName}
          title={t('title')}
          backButton={isAuthenticated && !companyName ? t('apply.back') : ''}
          logo={isMobile ? BoonViewIcon : BoonLogo}
          logoClassName={isMobile ? 'w-full h-full' : 'w-full h-[20px] rounded-none'}
        />
        <div className={styles.mainContainer}>
          <ReferralProcess />
          {shouldShowConnectNetwork && isMobile && (
            <button type="button" className={styles.backButton} onClick={() => setShouldShowConnectNetwork(false)}>
              <BackArrow className="w-[24px] h-[24px]" /> {t('title')}
            </button>
          )}
          <div
            className={styles.container}
            style={{
              padding: isAuthenticated && !isMobile ? '26px' : '0px',
              paddingBottom: isMobile ? '52px' : '26px',
            }}
          >
            {shouldShowConnectNetwork ? (
              <ConnectNetwork onClose={() => setShouldShowConnectNetwork(false)} />
            ) : (
              <>
                <ReferralFlowStepper currentStep={2} />

                <div className={styles.formSection}>
                  <div className={styles.formHeader}>
                    <h2>{t('candidateInfo.title')}</h2>
                  </div>

                  <div className={styles.yourInfo}>
                    <YourInfoSection personalInfo={personalInfo} setCurrentStep={setCurrentStep} />
                  </div>

                  <Formik
                    innerRef={formikRef}
                    enableReinitialize
                    initialValues={{
                      first_name: candidateInfo?.first_name || '',
                      last_name: candidateInfo?.last_name || '',
                      email: candidateInfo?.email || '',
                      phone_number: candidateInfo?.phone_number || '',
                      linkedin_url: candidateInfo?.linkedin_url || 'www.linkedin.com/in/',
                      user_contact_id: candidateInfo?.user_contact_id || null,
                      resume_files: candidateInfo?.resume_files || [],
                      ...buildCustomQuestionsInitialValues(),
                    }}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                  >
                    {({ setValues, values, setFieldValue, errors, submitCount }) => (
                      <Form>
                        <div className={styles.form}>
                          {isAuthenticated && (
                            <div className={styles.searchContainer}>
                              <ContactSelect
                                selectedContact={candidateInfo}
                                onSelect={(c) => {
                                  const newSearchParams = new URLSearchParams(searchParams);
                                  newSearchParams.delete('c_id');
                                  setSearchParams(newSearchParams);

                                  const currentValues = formikRef.current?.values || {};

                                  const updatedValues = {
                                    first_name: c.first_name || '',
                                    last_name: c.last_name || '',
                                    email: c.email || '',
                                    phone_number: c.phone_number || '',
                                    linkedin_url: c.linkedin_url?.replace(/^https?:\/\//, '') || 'www.linkedin.com/in/',
                                    user_contact_id: c.id || null,
                                    ...Object.keys(currentValues).reduce((acc, key) => {
                                      if (key.startsWith('custom_question_')) {
                                        acc[key] = currentValues[key];
                                      }
                                      return acc;
                                    }, {}),
                                  };

                                  setValues(updatedValues);
                                  setCandidateInfo(updatedValues);
                                }}
                                onConnectNetwork={() => setShouldShowConnectNetwork(true)}
                              />
                              <OrSeparator />
                            </div>
                          )}
                          <div className={styles.row}>
                            <FormikTextInput
                              data-testid="candidate-first-name"
                              name="first_name"
                              label={t('candidateInfo.firstName')}
                              placeholder={t('candidateInfo.firstNamePlaceholder')}
                              isRequired
                            />
                            <div className="md:mt-[27px]">
                              <FormikTextInput
                                data-testid="candidate-last-name"
                                name="last_name"
                                label={t('candidateInfo.lastName')}
                                placeholder={t('candidateInfo.lastNamePlaceholder')}
                                shouldDisplayLabel={false}
                              />
                            </div>
                          </div>
                          <FormikTextInput
                            data-testid="candidate-email"
                            name="email"
                            type="email"
                            label={t('candidateInfo.email')}
                            placeholder={t('candidateInfo.emailPlaceholder')}
                            isRequired
                          />
                          <FormikPhoneInput
                            data-testid="candidate-phone"
                            name="phone_number"
                            label={t('candidateInfo.phone')}
                            placeholder={t('candidateInfo.phonePlaceholder')}
                            isRequired={referralFormPermissions.phone_number_required}
                            isOptional={!referralFormPermissions.phone_number_required}
                          />
                          <FormikUrlInput
                            data-testid="candidate-linkedin"
                            name="linkedin_url"
                            label={t('candidateInfo.linkedIn')}
                            isLinkedIn
                            isRequired={referralFormPermissions.linkedin_url_required}
                            isOptional={!referralFormPermissions.linkedin_url_required}
                          />

                          <FileUpload
                            fieldName="resume_files"
                            files={values.resume_files}
                            setFieldValue={setFieldValue}
                            isRequired={referralFormPermissions.resume_required}
                            isOptional={!referralFormPermissions.resume_required}
                            error={submitCount > 0 ? errors.resume_files : undefined}
                            data-testid="candidate-resume-upload"
                          />

                          {referralFormPermissions.add_referral_form_custom_questions && (
                            <CustomQuestions customQuestions={referralFormPermissions.referral_form_custom_questions} />
                          )}
                        </div>
                        <div className={styles.nextButtonContainer}>
                          <ButtonWithIcon
                            data-testid="next-button"
                            type="submit"
                            variant="primary-36"
                            text={t('steps.next')}
                            textClassName="text-white"
                            containerClass="w-[142px] self-end"
                          />
                        </div>
                      </Form>
                    )}
                  </Formik>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
